import { LegacyProductCode } from '@up2stay/up2promo-api-client';
import { Link2, MapPin, UserRound } from 'lucide-react';

import { useDictionary } from '@/core/providers/dictionary-provider';
import {
  DestinationChoice,
  destinationChoicesForType,
  type DestinationChoiceId,
} from '@/routes/app/products-setup-wizard/products-setup-configure-flow';
import { ProductsSetupChoiceCard } from '@/routes/app/products-setup-wizard/products-setup-choice-card';
import { ProductsSetupStepShell } from '@/routes/app/products-setup-wizard/products-setup-step-shell';

function choiceMeta(
  choice: DestinationChoiceId,
  productType: LegacyProductCode | undefined,
  setup: Record<string, string | undefined>,
): { title: string; description: string; icon: typeof MapPin } {
  if (choice === DestinationChoice.Location) {
    return {
      title: setup.destinationLocationTitle ?? '',
      description: setup.destinationLocationDescription ?? '',
      icon: MapPin,
    };
  }
  if (choice === DestinationChoice.Username) {
    const isIg = productType === LegacyProductCode.IG;
    return {
      title: isIg
        ? (setup.destinationUsernameTitleIg ??
          setup.destinationUsernameTitle ??
          '')
        : (setup.destinationUsernameTitleTt ??
          setup.destinationUsernameTitle ??
          ''),
      description: setup.destinationUsernameDescription ?? '',
      icon: UserRound,
    };
  }
  return {
    title: setup.destinationCustomTitle ?? '',
    description: setup.destinationCustomDescription ?? '',
    icon: Link2,
  };
}

/**
 * Clear destination fork: location vs personal username vs custom link —
 * equal choice cards (no microscopic secondary links).
 */
export function ProductsSetupStepDestination({
  productType,
  onChoose,
}: {
  productType: LegacyProductCode | undefined;
  onChoose: (choice: DestinationChoiceId) => void;
}) {
  const dict = useDictionary();
  const setup =
    (dict as { products?: { setup?: Record<string, string> } }).products
      ?.setup ?? {};
  const choices = destinationChoicesForType(productType);

  const titleKey = productType
    ? `destinationTitle${productType}`
    : 'destinationTitle';
  const descriptionKey = productType
    ? `destinationDescription${productType}`
    : 'destinationDescription';

  return (
    <ProductsSetupStepShell
      title={setup[titleKey] ?? setup.destinationTitle ?? ''}
      description={setup[descriptionKey] ?? setup.destinationDescription}
    >
      <div
        className={
          choices.length >= 3
            ? 'grid grid-cols-1 gap-3 sm:gap-3.5 md:grid-cols-3 md:gap-4'
            : 'grid grid-cols-1 gap-3 sm:gap-3.5 md:grid-cols-2 md:gap-4'
        }
      >
        {choices.map((choice, index) => {
          const meta = choiceMeta(choice, productType, setup);
          return (
            <ProductsSetupChoiceCard
              key={choice}
              title={meta.title}
              description={meta.description}
              icon={meta.icon}
              emphasized={index === 0}
              onClick={() => onChoose(choice)}
            />
          );
        })}
      </div>
    </ProductsSetupStepShell>
  );
}
